import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

/**
 * ProjectCard: black & white image + white logo centered at the bottom.
 * Hover state: image fades into orange gradient, logo translates up,
 * and a "+" button reveals in the bottom-right corner.
 */
export function ProjectCard({ project, testId = 'project-card' }) {
  return (
    <Link
      to={`/proyectos/${project.slug}`}
      className="group relative block rounded-2xl overflow-hidden border border-[#3A4657]/15 hover:border-[#3A4657]/35 transition-colors duration-300"
      data-testid={`${testId}-${project.slug}`}
      aria-label={`Ver galería: ${project.title}`}
    >
      <div className="relative aspect-[4/3] bg-black overflow-hidden">
        {/* Background photo: B&W by default → original color on hover */}
        <img
          src={project.mainImage}
          alt={project.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover
                     transition-[transform,filter] duration-500
                     grayscale brightness-[0.8]
                     group-hover:grayscale-0 group-hover:brightness-100
                     group-hover:scale-[1.04]"
          style={{ objectPosition: project.coverPosition || 'center top' }}
        />

        {/* Orange gradient overlay (revealed on hover) */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              'linear-gradient(180deg, rgba(237,107,36,0.30) 0%, rgba(237,107,36,0.70) 55%, rgba(237,107,36,0.95) 100%)',
          }}
        />

        {/* Soft bottom darkening for logo readability when not hovered */}
        <div
          className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.55) 100%)',
          }}
        />

        {/* Client logo — standardized width (matches Condor reference), height proportional.
            Logos that are too tall (square-ish) are scaled down 30% to avoid eating vertical space. */}
        <div className="absolute inset-x-0 bottom-0 flex justify-center items-end pb-6 sm:pb-7 lg:pb-8 pointer-events-none">
          {project.clientLogo ? (
            (() => {
              const TALL_LOGOS = ['el-bosque.png', 'mg.png', 'adgemsa.png'];
              const isTall = TALL_LOGOS.some((n) => project.clientLogo.endsWith('/' + n));
              const sizeClass = isTall
                ? 'w-[77px] sm:w-[98px] lg:w-[112px]' // 30% smaller
                : 'w-[110px] sm:w-[140px] lg:w-[160px]';
              return (
                <img
                  src={project.clientLogo}
                  alt={project.client}
                  className={`${sizeClass} h-auto object-contain transition-transform duration-500 ease-out group-hover:-translate-y-3`}
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              );
            })()
          ) : (
            <span
              className="text-white font-bold uppercase tracking-[0.25em] text-sm sm:text-base transition-transform duration-500 ease-out group-hover:-translate-y-3"
            >
              {project.client}
            </span>
          )}
        </div>

        {/* "+" button — appears on hover, bottom-right */}
        <div
          className="absolute right-4 bottom-4 sm:right-5 sm:bottom-5 rounded-full bg-white/95 text-[#ED6B24] w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center shadow-md opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
          aria-hidden="true"
          data-testid={`${testId}-plus-${project.slug}`}
        >
          <Plus size={20} strokeWidth={3} />
        </div>
      </div>
    </Link>
  );
}
